import React, {
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { executeActions, getWidgetSpecificProps } from "@utils/index";
import { connect, useDispatch } from "react-redux";
import {
  BenefitIcon,
  BenefitItem,
  BenefitsList,
  BenefitsTitle,
  CardContent,
  CardDivider,
  CardHeader,
  ComparisonColumn,
  ComparisonGrid,
  ComparisonHeader,
  ComparisonLabel,
  ComparisonNote,
  ComparisonPriceLine,
  ComparisonPrice,
  ComparisonRowPrice,
  ComparisonSubText,
  ComparisonStrikePrice,
  ComparisonTotalLabel,
  ComparisonTotalPrice,
  ModalContentWrapper,
  ModalSavingsBadge,
  PlanCardButton,
  PlanExtensionTitle,
  PlanPrice,
  PlanPriceRow,
  PlanRadioWrapper,
  PlansListWrapper,
  SavingsAmount,
  SavingsCta,
  SavingsStrip,
  StrikeThroughPrice
} from "./styles";
import { useOnload } from "@utils/useOnLoad";
import { setPayloadData } from "@store/slices/globalState";
import DOMPurify from "isomorphic-dompurify";
import Modal from "@components/modal";
import Radio from "@components/common/radio";
import { getInrFormat } from "@utils/inrFormatter";
import { resolvePath } from "@utils/resolvePath";

type ActionConfig = {
  on_load?: unknown[];
  know_how_on_click?: unknown[];
  know_how_modal_on_load?: unknown[];
  know_how_modal_on_close?: unknown[];
  know_how_modal_confirm_on_click?: unknown[];
};

type PlanExtensionPlan = {
  id?: string;
  display_name?: string;
  sub_text?: string;
  display_premium?: string;
  premium?: number | string;
  price_difference?: number | string | null;
  strike_through_premium?: string;
  is_selected?: boolean;
  modal_price_breakup?: ModalPriceBreakupRow[];
};

type ModalPriceBreakupRow = {
  id?: string;
  price?: number | string;
  display_price?: string;
  strike_through_price?: number | string | null;
  display_strike_through_price?: string;
  tenure_label?: string;
};

type CoverageComparisonRow = {
  id?: string;
  label?: string;
  current?: string;
  extended?: string;
};

type PlanBenefitConfig =
  | string
  | {
      id?: string;
      text?: string;
      coverageRowId?: string;
    };

type ComparisonHeaders = {
  coverageType?: string;
  current?: string;
  extended?: string;
};

type PlanExtensionCardsListProps = {
  is_visible?: boolean;
  actions?: ActionConfig;
  plans?: PlanExtensionPlan[] | null;
  bottomNudge?: string;
  currentPlanTitle?: string;
  extendedPlanTitle?: string;
  benefitsTitle?: string;
  currentBenefits?: PlanBenefitConfig[];
  extendedBenefits?: PlanBenefitConfig[];
  knowHowLabel?: string;
  knowHowAriaLabel?: string;
  modalHeading?: string;
  modalPrimaryCtaText?: string;
  modalNote?: string;
  modalFallbackNote?: string;
  comparisonHeaders?: ComparisonHeaders;
  coverageRows?: CoverageComparisonRow[];
  totalPremiumLabel?: string;
  savingsBadgeSuffix?: string;
};

type OrderedPlan = {
  index: number;
  plan: PlanExtensionPlan;
  type: "current" | "extended";
};

type PlanExtensionCardProps = {
  title: string;
  is_visible?: boolean;
  actions?: ActionConfig;
  price: string;
  selected: boolean;
  bottomNudge?: string;
  onClick: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
  priceDiff: number | null;
  strikeThroughPrice?: string;
  variant: "current" | "extended";
  onKnowHowClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  benefitsTitle?: string;
  benefits: string[];
  knowHowLabel: string;
  knowHowAriaLabel: string;
};

type KnowHowModalProps = {
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
  currentPlan?: PlanExtensionPlan;
  extendedPlan?: PlanExtensionPlan;
  onConfirm: () => void;
  modalHeading: string;
  modalPrimaryCtaText: string;
  modalNote?: string;
  modalFallbackNote?: string;
  comparisonHeaders: Required<ComparisonHeaders>;
  coverageRows: Required<CoverageComparisonRow>[];
  totalPremiumLabel: string;
  savingsBadgeSuffix: string;
  onView: () => void;
};

const mapStateToProps = function (
  state: Record<string, unknown>,
  ownProps: { id: string }
) {
  return getWidgetSpecificProps(
    (state as { configuration?: { normalisedConfig?: unknown } }).configuration
      ?.normalisedConfig,
    ownProps.id
  );
};

const PlanExtensionCardsList = (props: PlanExtensionCardsListProps) => {
  const {
    is_visible,
    actions,
    plans,
    bottomNudge,
    currentPlanTitle = DEFAULT_CURRENT_PLAN_TITLE,
    extendedPlanTitle = DEFAULT_EXTENDED_PLAN_TITLE,
    benefitsTitle,
    currentBenefits,
    extendedBenefits,
    knowHowLabel = DEFAULT_KNOW_HOW_LABEL,
    knowHowAriaLabel = DEFAULT_KNOW_HOW_ARIA_LABEL,
    modalHeading = DEFAULT_MODAL_HEADING,
    modalPrimaryCtaText = DEFAULT_MODAL_PRIMARY_CTA_TEXT,
    modalNote,
    modalFallbackNote,
    comparisonHeaders,
    coverageRows,
    totalPremiumLabel = DEFAULT_TOTAL_PREMIUM_LABEL,
    savingsBadgeSuffix = DEFAULT_SAVINGS_BADGE_SUFFIX
  } = props;

  const { on_load } = actions || {};
  const {
    know_how_on_click,
    know_how_modal_on_load,
    know_how_modal_on_close,
    know_how_modal_confirm_on_click
  } = actions || {};

  useOnload({ on_load });
  const dispatch = useDispatch();

  const defaultSelectedIndex = useMemo(
    () => getDefaultSelectedIndex(plans),
    [plans]
  );
  const [selected, setSelected] = useState(defaultSelectedIndex);
  const [showKnowHowModal, setShowKnowHowModal] = useState(false);

  useEffect(() => {
    setSelected(defaultSelectedIndex);
  }, [defaultSelectedIndex]);

  const comparisonPlans = useMemo(() => getComparisonPlans(plans), [plans]);
  const resolvedCoverageRows = useMemo(
    () => getCoverageRows(coverageRows),
    [coverageRows]
  );
  const resolvedComparisonHeaders = useMemo(
    () => getComparisonHeaders(comparisonHeaders),
    [comparisonHeaders]
  );
  const visibleCoverageRowIds = useMemo(
    () =>
      getVisibleCoverageRowIds(
        comparisonPlans.currentPlan,
        comparisonPlans.extendedPlan,
        resolvedCoverageRows
      ),
    [
      comparisonPlans.currentPlan,
      comparisonPlans.extendedPlan,
      resolvedCoverageRows
    ]
  );
  const resolvedCurrentBenefits = useMemo(
    () =>
      getPlanBenefits(
        currentBenefits,
        visibleCoverageRowIds
      ),
    [currentBenefits, visibleCoverageRowIds]
  );
  const resolvedExtendedBenefits = useMemo(
    () =>
      getPlanBenefits(
        extendedBenefits,
        visibleCoverageRowIds
      ),
    [extendedBenefits, visibleCoverageRowIds]
  );

  useEffect(() => {
    const selectedPlanId = plans?.[selected]?.id;
    if (selectedPlanId) {
      dispatch(
        setPayloadData({
          plan_id: selectedPlanId
        })
      );
    }
  }, [dispatch, plans, selected]);

  const onSelect = (id: number) => {
    if (typeof id === "number" && id >= 0) {
      setSelected(id);
    }
  };

  const onSelectWithKeyboard = (
    event: KeyboardEvent<HTMLDivElement>,
    id: number
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect(id);
    }
  };

  const openKnowHowModal = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    executeActions(know_how_on_click);
    setShowKnowHowModal(true);
  };

  const selectExtendedPlan = () => {
    executeActions(know_how_modal_confirm_on_click);
    if (comparisonPlans.extendedIndex >= 0) {
      onSelect(comparisonPlans.extendedIndex);
    }
    setShowKnowHowModal(false);
  };

  const setKnowHowModalVisibility = (nextShowModal: boolean) => {
    if (!nextShowModal && showKnowHowModal) {
      executeActions(know_how_modal_on_close);
    }
    setShowKnowHowModal(nextShowModal);
  };

  return is_visible && Array.isArray(plans) && plans.length > 0 ? (
    <>
      <PlansListWrapper role="radiogroup" aria-label="Choose plan duration">
        {comparisonPlans.orderedPlans.map(
          ({ plan, index, type }: OrderedPlan) => (
            <PlanExtensionCard
              key={plan?.id || index}
              title={type === "extended" ? extendedPlanTitle : currentPlanTitle}
              price={getFormattedPlanPrice(plan)}
              strikeThroughPrice={
                type === "extended" ? getStrikeThroughPrice(plan) : ""
              }
              is_visible
              bottomNudge={
                type === "extended" ? getSavingsText(plan, bottomNudge) : ""
              }
              selected={selected === index}
              onClick={() => onSelect(index)}
              onKeyDown={(event) => onSelectWithKeyboard(event, index)}
              priceDiff={getNumericAmount(plan?.price_difference)}
              variant={type}
              onKnowHowClick={openKnowHowModal}
              benefitsTitle={benefitsTitle}
              benefits={
                type === "extended"
                  ? resolvedExtendedBenefits
                  : resolvedCurrentBenefits
              }
              knowHowLabel={knowHowLabel}
              knowHowAriaLabel={knowHowAriaLabel}
            />
          )
        )}
      </PlansListWrapper>
      <KnowHowModal
        showModal={showKnowHowModal}
        setShowModal={setKnowHowModalVisibility}
        currentPlan={comparisonPlans.currentPlan}
        extendedPlan={comparisonPlans.extendedPlan}
        onConfirm={selectExtendedPlan}
        modalHeading={modalHeading}
        modalPrimaryCtaText={modalPrimaryCtaText}
        modalNote={modalNote}
        modalFallbackNote={modalFallbackNote}
        comparisonHeaders={resolvedComparisonHeaders}
        coverageRows={resolvedCoverageRows}
        totalPremiumLabel={totalPremiumLabel}
        savingsBadgeSuffix={savingsBadgeSuffix}
        onView={() => executeActions(know_how_modal_on_load)}
      />
    </>
  ) : (
    <></>
  );
};

export const PlanExtensionCard = (props: PlanExtensionCardProps) => {
  const {
    title,
    is_visible,
    actions,
    price,
    selected,
    bottomNudge,
    onClick,
    onKeyDown,
    priceDiff,
    strikeThroughPrice,
    variant,
    onKnowHowClick,
    benefitsTitle,
    benefits,
    knowHowLabel,
    knowHowAriaLabel
  } = props;

  const { on_load } = actions || {};

  useOnload({ on_load });

  return is_visible ? (
    <PlanCardButton
      role="radio"
      tabIndex={0}
      aria-checked={selected}
      selected={selected}
      variant={variant}
      onClick={onClick}
      onKeyDown={onKeyDown}
    >
      <CardContent>
        <CardHeader>
          <div>
            <PlanExtensionTitle>{title}</PlanExtensionTitle>
            <PlanPriceRow>
              <PlanPrice>{price}</PlanPrice>
              {strikeThroughPrice ? (
                <StrikeThroughPrice>{strikeThroughPrice}</StrikeThroughPrice>
              ) : null}
            </PlanPriceRow>
          </div>
          <PlanRadioWrapper aria-hidden>
            <Radio isSelected={selected} size={16} />
          </PlanRadioWrapper>
        </CardHeader>
        {benefits.length ? (
          <>
            <CardDivider />
            {benefitsTitle ? (
              <BenefitsTitle>{benefitsTitle}</BenefitsTitle>
            ) : null}
            <BenefitsList>
              {benefits.map((benefit) => (
                <BenefitItem key={benefit}>
                  <BenefitIcon
                    src={resolvePath("/images/Check_tick_correct.svg")}
                    alt=""
                  />
                  <span
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(benefit)
                    }}
                  />
                </BenefitItem>
              ))}
            </BenefitsList>
          </>
        ) : null}
      </CardContent>
      {bottomNudge ? (
        <SavingsStrip>
          <span>{renderSavingsText(bottomNudge, priceDiff)}</span>
          <SavingsCta
            type="button"
            onClick={onKnowHowClick}
            onKeyDown={(event) => event.stopPropagation()}
            onKeyUp={(event) => event.stopPropagation()}
            aria-label={knowHowAriaLabel}
          >
            {knowHowLabel}
          </SavingsCta>
        </SavingsStrip>
      ) : null}
    </PlanCardButton>
  ) : (
    <></>
  );
};

const DEFAULT_CURRENT_PLAN_TITLE = "Selected coverage";
const DEFAULT_EXTENDED_PLAN_TITLE = "Extended coverage";
const DEFAULT_KNOW_HOW_LABEL = "Know how";
const DEFAULT_KNOW_HOW_ARIA_LABEL = "Know how this 3 year plan saves money";
const DEFAULT_MODAL_HEADING = "Save more by opting for 3 year plan";
const DEFAULT_MODAL_PRIMARY_CTA_TEXT = "Confirm";
const DEFAULT_TOTAL_PREMIUM_LABEL = "Total premium";
const DEFAULT_SAVINGS_BADGE_SUFFIX = "less";

const DEFAULT_COMPARISON_HEADERS: Required<ComparisonHeaders> = {
  coverageType: "Coverage type",
  current: "Current Coverage",
  extended: "Extended 3-year plan"
};

const DEFAULT_COVERAGE_ROWS: Required<CoverageComparisonRow>[] = [
  {
    id: "third_party",
    label: "Third-party liability (TP)",
    current: "3 year",
    extended: "3 year"
  },
  {
    id: "own_damage",
    label: "Own damage (OD)",
    current: "1 year",
    extended: "3 year"
  },
  {
    id: "zero_depreciation",
    label: "Zero depreciation",
    current: "1 year",
    extended: "3 year"
  },
  {
    id: "additional_covers",
    label: "Additional covers",
    current: "1 year",
    extended: "3 year"
  }
];

const getPlanBenefitText = (benefit: PlanBenefitConfig): string => {
  if (typeof benefit === "string") return benefit.trim();
  return typeof benefit?.text === "string" ? benefit.text.trim() : "";
};

const getPlanBenefitCoverageRowId = (
  benefit: PlanBenefitConfig
): string | undefined => {
  return typeof benefit === "string" ? undefined : benefit.coverageRowId;
};

const getPlanBenefits = (
  benefits: PlanBenefitConfig[] | undefined,
  visibleCoverageRowIds: Set<string>
): string[] => {
  if (!Array.isArray(benefits) || benefits.length === 0) return [];

  return benefits
    .filter((benefit) => {
      const coverageRowId = getPlanBenefitCoverageRowId(benefit);
      return !coverageRowId || visibleCoverageRowIds.has(coverageRowId);
    })
    .map(getPlanBenefitText)
    .filter(Boolean);
};

const getNumericAmount = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const sanitizedValue = value.replace(/,/g, "").replace(/[^\d.-]/g, "");
    if (!sanitizedValue.trim()) return null;
    const amount = Number(sanitizedValue);
    return Number.isFinite(amount) ? amount : null;
  }
  return null;
};

const formatCurrency = (value: unknown): string => {
  const amount = getNumericAmount(value);
  if (amount === null) return "";
  if (amount === 0) return "₹0";
  return `₹${getInrFormat(amount)}`;
};

const getFormattedPlanPrice = (plan?: PlanExtensionPlan): string => {
  return getFormattedPriceValue(plan?.display_premium, plan?.premium);
};

const getStrikeThroughPrice = (plan?: PlanExtensionPlan): string => {
  if (
    typeof plan?.strike_through_premium === "string" &&
    plan.strike_through_premium.trim()
  ) {
    const strikeThroughPrice = plan.strike_through_premium.trim();
    return strikeThroughPrice.includes("₹")
      ? strikeThroughPrice
      : `₹${strikeThroughPrice}`;
  }

  const premium = getNumericAmount(plan?.premium);
  const priceDiff = getNumericAmount(plan?.price_difference);
  if (!premium || !priceDiff) return "";
  return formatCurrency(premium + priceDiff);
};

const getSavingsText = (
  plan: PlanExtensionPlan,
  bottomNudge?: string
): string => {
  const priceDiff = getNumericAmount(plan?.price_difference);
  if (!priceDiff) return "";
  return (
    bottomNudge?.replaceAll("{price_diff}", getInrFormat(priceDiff)) ||
    `${getInrFormat(priceDiff)}`
  );
};

const renderSavingsText = (
  savingsText?: string,
  priceDiff?: number | null
): React.ReactNode => {
  const text =
    savingsText ||
    (priceDiff ? `Save ${formatCurrency(priceDiff)} in 3 year coverage` : "");
  const match = text.match(/₹[\d,]+/);

  if (!match) return text;

  const [amount] = match;
  const [prefix, suffix] = text.split(amount);

  return (
    <>
      {prefix}
      <SavingsAmount>{amount}</SavingsAmount>
      {suffix}
    </>
  );
};

const renderTemplate = (
  template: string,
  values: Record<string, string>
): string => {
  return Object.entries(values).reduce(
    (resolvedTemplate, [key, value]) =>
      resolvedTemplate.replaceAll(`{${key}}`, value),
    template
  );
};

const getFormattedPriceValue = (
  displayValue?: unknown,
  numericValue?: unknown
): string => {
  if (typeof displayValue === "string" && displayValue.trim()) {
    const displayPrice = displayValue.trim();
    return displayPrice.includes("₹") ? displayPrice : `₹${displayPrice}`;
  }
  return formatCurrency(numericValue);
};

const getCoverageRows = (
  rows: CoverageComparisonRow[] | undefined
): Required<CoverageComparisonRow>[] => {
  if (!Array.isArray(rows) || rows.length === 0) return DEFAULT_COVERAGE_ROWS;

  const resolvedRows = rows
    .filter((row) => row?.label && row?.current && row?.extended)
    .map((row, index) => ({
      id: row.id ? String(row.id) : DEFAULT_COVERAGE_ROWS[index]?.id || "",
      label: String(row.label),
      current: String(row.current),
      extended: String(row.extended)
    }));

  return resolvedRows.length > 0 ? resolvedRows : DEFAULT_COVERAGE_ROWS;
};

const getComparisonHeaders = (
  comparisonHeaders?: ComparisonHeaders
): Required<ComparisonHeaders> => {
  return {
    coverageType:
      comparisonHeaders?.coverageType ||
      DEFAULT_COMPARISON_HEADERS.coverageType,
    current: comparisonHeaders?.current || DEFAULT_COMPARISON_HEADERS.current,
    extended: comparisonHeaders?.extended || DEFAULT_COMPARISON_HEADERS.extended
  };
};

const getModalPriceBreakupForRow = (
  plan: PlanExtensionPlan | undefined,
  row: Required<CoverageComparisonRow>
): ModalPriceBreakupRow | undefined => {
  return plan?.modal_price_breakup?.find(
    (priceBreakupRow) => priceBreakupRow?.id === row.id
  );
};

const getModalPriceBreakupAmount = (
  priceBreakup?: ModalPriceBreakupRow
): number | null => {
  const priceAmount = getNumericAmount(priceBreakup?.price);
  if (priceAmount !== null) return priceAmount;

  return getNumericAmount(priceBreakup?.display_price);
};

const hasZeroComparisonAmount = (
  plan: PlanExtensionPlan | undefined,
  row: Required<CoverageComparisonRow>
): boolean => {
  const priceBreakup = getModalPriceBreakupForRow(plan, row);
  return getModalPriceBreakupAmount(priceBreakup) === 0;
};

const shouldRenderCoverageRow = (
  currentPlan: PlanExtensionPlan | undefined,
  extendedPlan: PlanExtensionPlan | undefined,
  row: Required<CoverageComparisonRow>
): boolean => {
  return (
    !hasZeroComparisonAmount(currentPlan, row) &&
    !hasZeroComparisonAmount(extendedPlan, row)
  );
};

const getVisibleCoverageRowIds = (
  currentPlan: PlanExtensionPlan | undefined,
  extendedPlan: PlanExtensionPlan | undefined,
  coverageRows: Required<CoverageComparisonRow>[]
): Set<string> => {
  return new Set(
    coverageRows
      .filter((row) => shouldRenderCoverageRow(currentPlan, extendedPlan, row))
      .map((row) => row.id)
  );
};

const getComparisonCellData = (
  plan: PlanExtensionPlan | undefined,
  row: Required<CoverageComparisonRow>,
  fallbackTenure: string
) => {
  const priceBreakup = getModalPriceBreakupForRow(plan, row);

  return {
    price: getFormattedPriceValue(
      priceBreakup?.display_price,
      priceBreakup?.price
    ),
    strikeThroughPrice: getFormattedPriceValue(
      priceBreakup?.display_strike_through_price,
      priceBreakup?.strike_through_price
    ),
    tenureLabel: priceBreakup?.tenure_label || fallbackTenure
  };
};

const ComparisonCell = ({
  price,
  strikeThroughPrice,
  tenureLabel
}: {
  price: string;
  strikeThroughPrice: string;
  tenureLabel: string;
}) => (
  <>
    {price ? (
      <ComparisonPriceLine>
        <ComparisonRowPrice>{price}</ComparisonRowPrice>
        {strikeThroughPrice ? (
          <ComparisonStrikePrice>{strikeThroughPrice}</ComparisonStrikePrice>
        ) : null}
      </ComparisonPriceLine>
    ) : null}
    {tenureLabel ? (
      <ComparisonSubText>({tenureLabel})</ComparisonSubText>
    ) : null}
  </>
);

const getDefaultSelectedIndex = (
  plans: PlanExtensionPlan[] | null | undefined = []
): number => {
  if (!Array.isArray(plans) || plans.length === 0) return 0;
  const selectedIndex = plans.findIndex((plan) => plan?.is_selected);
  return selectedIndex >= 0 ? selectedIndex : 0;
};

const isExtendedPlan = (plan: PlanExtensionPlan): boolean => {
  const id = String(plan?.id || "").toLowerCase();
  const displayName = String(plan?.display_name || "").toLowerCase();
  return id.includes("three_plus_three") || displayName.includes("3+3");
};

const getComparisonPlans = (
  plans: PlanExtensionPlan[] | null | undefined = []
) => {
  if (!Array.isArray(plans) || plans.length === 0) {
    return {
      currentPlan: undefined,
      extendedPlan: undefined,
      currentIndex: -1,
      extendedIndex: -1,
      orderedPlans: []
    };
  }

  const extendedIndexById = plans.findIndex(isExtendedPlan);
  const selectedCurrentIndex = plans.findIndex(
    (plan) => plan?.is_selected && !isExtendedPlan(plan)
  );
  const currentIndexByPlanType = plans.findIndex(
    (plan) => !isExtendedPlan(plan)
  );
  const fallbackCurrentIndex =
    selectedCurrentIndex >= 0
      ? selectedCurrentIndex
      : currentIndexByPlanType >= 0
        ? currentIndexByPlanType
        : 0;
  const extendedIndexByPriceDiff = plans.findIndex(
    (plan, index) =>
      index !== fallbackCurrentIndex && getNumericAmount(plan?.price_difference)
  );
  const fallbackExtendedIndex = plans.findIndex(
    (_, index) => index !== fallbackCurrentIndex
  );
  const extendedIndex =
    extendedIndexById >= 0 && extendedIndexById !== fallbackCurrentIndex
      ? extendedIndexById
      : extendedIndexByPriceDiff >= 0
        ? extendedIndexByPriceDiff
        : fallbackExtendedIndex;

  const currentPlan = plans[fallbackCurrentIndex] || plans[0];
  const extendedPlan =
    plans[extendedIndex] ||
    plans.find((_, index) => index !== fallbackCurrentIndex) ||
    currentPlan;
  const orderedIndexes = [fallbackCurrentIndex, extendedIndex].filter(
    (index, position, list) => index >= 0 && list.indexOf(index) === position
  );

  return {
    currentPlan,
    extendedPlan,
    currentIndex: fallbackCurrentIndex,
    extendedIndex,
    orderedPlans: orderedIndexes.map(
      (index): OrderedPlan => ({
        index,
        plan: plans[index],
        type: index === extendedIndex ? "extended" : "current"
      })
    )
  };
};

const KnowHowModal = ({
  showModal,
  setShowModal,
  currentPlan,
  extendedPlan,
  onConfirm,
  modalHeading,
  modalPrimaryCtaText,
  modalNote,
  modalFallbackNote,
  comparisonHeaders,
  coverageRows,
  totalPremiumLabel,
  savingsBadgeSuffix,
  onView
}: KnowHowModalProps) => {
  const savingsAmount = getNumericAmount(extendedPlan?.price_difference);
  const savingsText = formatCurrency(savingsAmount);
  const note =
    savingsAmount && modalNote
      ? renderTemplate(modalNote, { savings: savingsText })
      : modalFallbackNote || "";
  const visibleCoverageRows = coverageRows.filter((row) =>
    shouldRenderCoverageRow(currentPlan, extendedPlan, row)
  );
  const hasTrackedModalView = useRef(false);

  useEffect(() => {
    if (showModal && !hasTrackedModalView.current) {
      onView();
      hasTrackedModalView.current = true;
    }

    if (!showModal) {
      hasTrackedModalView.current = false;
    }
  }, [onView, showModal]);

  return (
    <Modal
      showModal={showModal}
      setShowModal={setShowModal}
      modalName="PlanExtensionKnowHowModal"
      modalHeading={modalHeading}
      actions={{}}
      modalHeadingStyles={{
        fontSize: "18px",
        lineHeight: "30px",
        letterSpacing: "-0.1px",
        maxWidth: "100%",
        paddingLeft: "4px",
        paddingRight: "40px",
        boxSizing: "border-box",
        marginTop: "10px",
        whiteSpace: "nowrap"
      }}
      wrapperStyles={{
        borderTopLeftRadius: "24px",
        borderTopRightRadius: "24px",
        borderBottomLeftRadius: "0",
        borderBottomRightRadius: "0",
        left: "0",
        maxWidth: "none",
        padding: "30px 20px 0",
        right: "0",
        width: "100vw"
      }}
      styles={{
        maxWidth: "504px"
      }}
      closeButtonStyles={{
        display: "none"
      }}
      showModalFooter
      showPrimaryCta
      primaryCtaText={modalPrimaryCtaText}
      primaryCtaClickHandler={onConfirm}
      footerWrapperStyles={{ marginTop: "0", padding: "12px 0 20px" }}
      footerWrapperDStyles={{ marginTop: "0", padding: "12px 0 0" }}
    >
      <ModalContentWrapper>
        <ComparisonGrid>
          <ComparisonLabel isHeader>
            {comparisonHeaders.coverageType}
          </ComparisonLabel>
          <ComparisonHeader>{comparisonHeaders.current}</ComparisonHeader>
          <ComparisonHeader isExtended>
            {comparisonHeaders.extended}
          </ComparisonHeader>

          {visibleCoverageRows.map((row, index) => {
            const isLastCoverageRow = index === visibleCoverageRows.length - 1;

            return (
              <React.Fragment key={row.label}>
                <ComparisonLabel isLastCoverageRow={isLastCoverageRow}>
                  {row.label}
                </ComparisonLabel>
                <ComparisonColumn isLastCoverageRow={isLastCoverageRow}>
                  <ComparisonCell
                    {...getComparisonCellData(currentPlan, row, row.current)}
                  />
                </ComparisonColumn>
                <ComparisonColumn
                  isExtended
                  isLastCoverageRow={isLastCoverageRow}
                >
                  <ComparisonCell
                    {...getComparisonCellData(extendedPlan, row, row.extended)}
                  />
                </ComparisonColumn>
              </React.Fragment>
            );
          })}

          <ComparisonTotalLabel>{totalPremiumLabel}</ComparisonTotalLabel>
          <ComparisonTotalPrice>
            {getFormattedPlanPrice(currentPlan)}
          </ComparisonTotalPrice>
          <ComparisonTotalPrice isExtended>
            {savingsText ? (
              <ModalSavingsBadge>
                {savingsText} {savingsBadgeSuffix}
              </ModalSavingsBadge>
            ) : null}
            <ComparisonPrice>
              {getFormattedPlanPrice(extendedPlan)}
            </ComparisonPrice>
          </ComparisonTotalPrice>
        </ComparisonGrid>
        {note ? <ComparisonNote>{note}</ComparisonNote> : null}
      </ModalContentWrapper>
    </Modal>
  );
};

export default connect(mapStateToProps)(PlanExtensionCardsList);
